import { SignInResource } from '@clerk/types';
import { detectEnvironment, getClerkStrategy } from './utils/platformDetection';
import { AuthEvent, AuthResult } from './types';

export class AutoLoginCoordinator {
  private signIn: SignInResource | null = null;
  private onEvent: (event: AuthEvent) => void;

  constructor(signIn: SignInResource | null, onEvent: (event: AuthEvent) => void) {
    this.signIn = signIn;
    this.onEvent = onEvent;
  }

  /**
   * Executes the full auth chain: detection → platform → os → fallback
   */
  async executeAuthChain(): Promise<void> {
    // Phase 1: Detection
    const environment = detectEnvironment();
    this.emitEvent({
      timestamp: new Date(),
      stage: 'detection',
      status: 'info',
      message: `Environment detected: ${environment.isMobile ? 'Mobile' : 'Desktop'}`,
      details: {
        isMobile: environment.isMobile,
        platform: environment.platform,
        os: environment.os,
        userAgent: environment.userAgent,
      },
    });

    console.log('[AutoLogin] 🔍 Detection:', {
      isMobile: environment.isMobile,
      platform: environment.platform,
      os: environment.os,
    });

    // Desktop path: show auth picker immediately
    if (!environment.isMobile) {
      this.emitEvent({
        timestamp: new Date(),
        stage: 'fallback',
        status: 'info',
        message: 'Desktop detected - showing auth picker',
        details: { reason: 'desktop' },
      });
      console.log('[AutoLogin] 🖥️ Desktop detected, showing auth picker');
      return;
    }

    // Mobile path: try platform → os → fallback
    // Phase 2: Try platform-specific OAuth
    if (environment.platform) {
      const platformResult = await this.tryPlatformAuth(environment.platform);
      if (platformResult.success) {
        return; // OAuth redirect started, will complete after redirect
      }
    }

    // Phase 3: Try OS-based OAuth
    if (environment.os) {
      const osResult = await this.tryOSAuth(environment.os);
      if (osResult.success) {
        return; // OAuth redirect started, will complete after redirect
      }
    }

    // Phase 4: Fallback to manual auth picker
    this.emitEvent({
      timestamp: new Date(),
      stage: 'fallback',
      status: 'info',
      message: 'All auto-login methods failed - showing auth picker',
      details: { reason: 'all_methods_failed' },
    });
    console.log('[AutoLogin] 📋 All auto-methods failed, showing auth picker');
  }

  /**
   * Attempts to authenticate using platform-specific OAuth (Instagram, TikTok, etc.)
   */
  private async tryPlatformAuth(platform: string): Promise<AuthResult> {
    const strategy = getClerkStrategy(platform);

    this.emitEvent({
      timestamp: new Date(),
      stage: 'platform_attempt',
      status: 'info',
      message: `Attempting ${platform} OAuth`,
      details: { platform, strategy },
    });

    console.log('[AutoLogin] 🔄 Attempting:', `platform=${platform}`, `strategy=${strategy}`);

    if (!strategy) {
      const errorMsg = `No Clerk strategy mapping for platform: ${platform}`;
      this.emitEvent({
        timestamp: new Date(),
        stage: 'platform_attempt',
        status: 'error',
        message: errorMsg,
        details: { platform },
      });
      console.error('[AutoLogin] ❌ Failed:', 'NO_STRATEGY_MAPPING', errorMsg);
      return { success: false, error: 'NO_STRATEGY_MAPPING', message: errorMsg };
    }

    if (!this.signIn) {
      const errorMsg = 'SignIn object not available';
      this.emitEvent({
        timestamp: new Date(),
        stage: 'platform_attempt',
        status: 'error',
        message: errorMsg,
      });
      console.error('[AutoLogin] ❌ Failed:', 'NO_SIGNIN', errorMsg);
      return { success: false, error: 'NO_SIGNIN', message: errorMsg };
    }

    try {
      // Use 'as any' to bypass TypeScript checking since we want to try all strategies
      // and let Clerk's runtime validation tell us if it's enabled
      await this.signIn.authenticateWithRedirect({
        strategy: strategy as any,
        redirectUrl: window.location.href,
        redirectUrlComplete: window.location.href,
      });

      // If we reach here, redirect started successfully
      this.emitEvent({
        timestamp: new Date(),
        stage: 'platform_attempt',
        status: 'success',
        message: `${platform} OAuth redirect initiated`,
        details: { platform, strategy },
      });

      console.log('[AutoLogin] ✅ Success:', `${platform} OAuth redirect started`);
      return { success: true };
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : String(err);

      // Check if provider is not configured in Clerk
      if (
        errorMessage.includes('not enabled') ||
        errorMessage.includes('not configured') ||
        errorMessage.includes('not found') ||
        errorMessage.includes('does not match one of the allowed values') ||
        errorMessage.includes('not available')
      ) {
        const msg = `${platform} OAuth is not enabled in Clerk dashboard`;
        this.emitEvent({
          timestamp: new Date(),
          stage: 'platform_attempt',
          status: 'error',
          message: msg,
          details: { platform, clerkError: errorMessage },
        });
        console.error('[AutoLogin] ❌ Failed:', 'PROVIDER_NOT_CONFIGURED', msg, { clerkError: errorMessage });
        return {
          success: false,
          error: 'PROVIDER_NOT_CONFIGURED',
          message: msg,
          details: { clerkError: errorMessage },
        };
      }

      // Generic OAuth error
      this.emitEvent({
        timestamp: new Date(),
        stage: 'platform_attempt',
        status: 'error',
        message: `${platform} OAuth failed: ${errorMessage}`,
        details: { platform, error: errorMessage },
      });
      console.error('[AutoLogin] ❌ Failed:', 'OAUTH_ERROR', errorMessage);
      return { success: false, error: 'OAUTH_ERROR', message: errorMessage };
    }
  }

  /**
   * Attempts to authenticate using OS-based OAuth (Apple for iOS, Google for Android)
   */
  private async tryOSAuth(os: 'ios' | 'android'): Promise<AuthResult> {
    const provider = os === 'ios' ? 'apple' : 'google';
    const strategy = getClerkStrategy(provider);

    this.emitEvent({
      timestamp: new Date(),
      stage: 'os_attempt',
      status: 'info',
      message: `Attempting ${provider} OAuth (${os})`,
      details: { os, provider, strategy },
    });

    console.log('[AutoLogin] 🔄 Attempting:', `os=${os}`, `provider=${provider}`, `strategy=${strategy}`);

    if (!strategy) {
      const errorMsg = `No Clerk strategy mapping for OS provider: ${provider}`;
      this.emitEvent({
        timestamp: new Date(),
        stage: 'os_attempt',
        status: 'error',
        message: errorMsg,
        details: { os, provider },
      });
      console.error('[AutoLogin] ❌ Failed:', 'NO_STRATEGY_MAPPING', errorMsg);
      return { success: false, error: 'NO_STRATEGY_MAPPING', message: errorMsg };
    }

    if (!this.signIn) {
      const errorMsg = 'SignIn object not available';
      this.emitEvent({
        timestamp: new Date(),
        stage: 'os_attempt',
        status: 'error',
        message: errorMsg,
      });
      console.error('[AutoLogin] ❌ Failed:', 'NO_SIGNIN', errorMsg);
      return { success: false, error: 'NO_SIGNIN', message: errorMsg };
    }

    try {
      // Use 'as any' to bypass TypeScript checking since we want to try all strategies
      // and let Clerk's runtime validation tell us if it's enabled
      await this.signIn.authenticateWithRedirect({
        strategy: strategy as any,
        redirectUrl: window.location.href,
        redirectUrlComplete: window.location.href,
      });

      // If we reach here, redirect started successfully
      this.emitEvent({
        timestamp: new Date(),
        stage: 'os_attempt',
        status: 'success',
        message: `${provider} OAuth redirect initiated`,
        details: { os, provider, strategy },
      });

      console.log('[AutoLogin] ✅ Success:', `${provider} OAuth redirect started`);
      return { success: true };
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : String(err);

      // Check if provider is not configured in Clerk
      if (
        errorMessage.includes('not enabled') ||
        errorMessage.includes('not configured') ||
        errorMessage.includes('not found') ||
        errorMessage.includes('does not match one of the allowed values') ||
        errorMessage.includes('not available')
      ) {
        const msg = `${provider} OAuth is not enabled in Clerk dashboard`;
        this.emitEvent({
          timestamp: new Date(),
          stage: 'os_attempt',
          status: 'error',
          message: msg,
          details: { os, provider, clerkError: errorMessage },
        });
        console.error('[AutoLogin] ❌ Failed:', 'PROVIDER_NOT_CONFIGURED', msg, { clerkError: errorMessage });
        return {
          success: false,
          error: 'PROVIDER_NOT_CONFIGURED',
          message: msg,
          details: { clerkError: errorMessage },
        };
      }

      // Generic OAuth error
      this.emitEvent({
        timestamp: new Date(),
        stage: 'os_attempt',
        status: 'error',
        message: `${provider} OAuth failed: ${errorMessage}`,
        details: { os, provider, error: errorMessage },
      });
      console.error('[AutoLogin] ❌ Failed:', 'OAUTH_ERROR', errorMessage);
      return { success: false, error: 'OAUTH_ERROR', message: errorMessage };
    }
  }

  private emitEvent(event: AuthEvent): void {
    this.onEvent(event);
  }
}
